import {useMutation} from '@apollo/client';
import {Box, IconButton, Typography} from '@mui/material';
import React, {useCallback, useMemo} from 'react';
import {GridColDef, GridRenderCellParams} from '@mui/x-data-grid-pro';
import {NumericFormat} from 'react-number-format';
import DeleteIcon from '@mui/icons-material/Delete';
import {useAccountContext} from '../../contexts/AccountContext';
import {Pot} from '../../schema-types/Pot';
import {ShowIfDebugWrapper} from '../../contexts/DebugProvider';
import {DELETE_POT, LIST_POTS} from '../../features/potQueries';
import {ACTIVE_PLAN_STATUSES} from '../../utils/potsFilter';

export type AccountPlanColumnProps = GridColDef & {
  field: keyof Pot;
};

const marginSpacing = 1;
const childMarginSpacing = 3;

export const useAccountPlanColumns = () => {
  const [deletePot] = useMutation(DELETE_POT);
  const {accountId, account} = useAccountContext();

  // Unlike `isSelectableSourcePlanStatus`, a missing status is treated as NOT active
  // here: this only highlights confirmed in-force/proposed SPP plans.
  const isActivePlanStatus = (pot: Pot) =>
    ACTIVE_PLAN_STATUSES.includes(pot.planStatus?.toLocaleLowerCase() ?? '');

  const getDisplayedPlanNumber = useCallback(
    (pot: Pot) => {
      const isSPP = account?.propositionType === 'SPP';
      // SPP quote the proposal ref for payments in; show the plan number when Curo has none
      if (isSPP && isActivePlanStatus(pot) && pot.proposalReference) {
        return pot.proposalReference;
      }
      // SFP, not active, or no proposal ref
      return pot.planNumber;
    },
    [account]
  );

  const PlanName: React.FC<{pot: Pot}> = ({pot}) => {
    return (
      <Box sx={{marginRight: marginSpacing}}>
        <Typography>{getDisplayedPlanNumber(pot)}</Typography>
      </Box>
    );
  };

  const accountPlanColumns: AccountPlanColumnProps[] = useMemo(() => {
    return [
      {
        field: 'productName',
        sortable: false,
        headerName: 'Plan Name',
        renderCell: (params) => {
          const row = params.row as Pot;
          return (
            <Box
              sx={{
                marginLeft:
                  params.row.parent || row.name === undefined
                    ? childMarginSpacing
                    : marginSpacing,
                marginRight: marginSpacing,
              }}
            >
              <Typography>
                <>{params.value}</>
              </Typography>
              <ShowIfDebugWrapper>{row.curoId || 'No curo ID'}</ShowIfDebugWrapper>
            </Box>
          );
        },
        valueGetter: (value, row, column, apiRef) => {
          const data = row as Pot;
          return data?.productName?.formattedName
            ?.toLowerCase()
            .replace('gia', 'GIA')
            .replace('isa', 'ISA')
            .replace('sipp', 'SIPP')
            .replace('-', ' ');
        },
        width: 180,
      },
      {
        field: 'planNumber',
        sortable: false,
        headerName: 'Plan Number',
        align: 'left',
        width: 120,
        renderCell: (params) => {
          const data = params.row as Pot;
          return <PlanName pot={data} />;
        },
      },
      // {
      //   field: 'ownership',
      //   sortable: false,
      //   headerName: 'Ownership',
      //   align: 'left',
      //   width: 160,
      //   valueGetter: (value, row, column, apiRef) => {
      //     const data = row as Pot;
      //     return data?.ownership;
      //   },
      // },
      {
        field: 'productProvider',
        sortable: false,
        headerName: 'Plan Provider',
        renderCell: (params) => {
          const data = params.row as Pot;
          return (
            <Box sx={{marginLeft: marginSpacing, marginRight: marginSpacing}}>
              <Typography>
                {data?.productProvider?.formattedName?.toLowerCase()}
              </Typography>
            </Box>
          );
        },
        flex: 2,
      },
      {
        field: 'mandate',
        sortable: false,
        headerName: 'Mandate',
        renderCell: (params) => {
          const data = params.row as Pot;
          const mandate = data.modelPortfolio?.name || data.mandate;
          if (!mandate) {
            return null;
          }
          return (
            <Box sx={{marginLeft: marginSpacing, marginRight: marginSpacing}}>
              <Typography>{mandate}</Typography>
              {/* TODO: .fee has been removed from Pot types! */}
              {/* <ShowIfDebugWrapper>Fee: {data.fee}</ShowIfDebugWrapper> */}
            </Box>
          );
        },
        flex: 2,
      },
      {
        field: 'contact',
        sortable: false,
        headerName: 'Contact',
        valueGetter: (value, row, column, apiRef) => {
          const data = row as Pot;
          if (data.contact?.id) {
            return data.contact.simplifiedName;
          }
          if ('name' in data) {
            // if not created by crystallised/uncrystallised rows, then it should have name, even if empty
            return 'Joint';
          }
          return '';
        },
        flex: 1,
      },
      {
        field: 'totalValue',
        sortable: false,
        headerName: 'Value',
        flex: 1.4,
        renderCell: (params) => {
          return (
            <NumericFormat
              displayType="text"
              value={params.value || ''}
              allowLeadingZeros={false}
              prefix="£"
              thousandSeparator=","
              decimalScale={2}
              readOnly
            />
          );
        },
      },
    ];
  }, [getDisplayedPlanNumber, account]);

  const accountPlanColumnsWithDelete = useMemo(
    () => [
      ...accountPlanColumns,
      {
        field: 'isCash', // Just tasking a random field to make this work
        headerName: '',
        renderCell: (params: GridRenderCellParams) => {
          const data = params.row as Pot;
          return (
            <IconButton
              onClick={() => {
                deletePot({
                  variables: {
                    potId: data.id,
                  },
                  refetchQueries: [
                    {
                      query: LIST_POTS,
                      variables: {
                        accountId,
                        parentOnly: true,
                        curoSync: false,
                      },
                    },
                  ],
                });
              }}
              size="large"
            >
              <DeleteIcon />
            </IconButton>
          );
        },
        flex: 0.5,
      },
    ],
    [accountPlanColumns, deletePot, accountId]
  );
  return {
    accountPlanColumns,
    accountPlanColumnsWithDelete,
  };
};
